import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { useToast } from 'vue-toastification'
import type { CartItem, Coupon, CheckoutData, Product } from '@/types'

const CART_STORAGE_KEY = 'ergonlider_cart'

interface StoredCart {
  items: CartItem[]
  coupon: Coupon | null
}

function isStandardItem(item: { purchase_mode?: string }): boolean {
  const mode = item.purchase_mode
  return !mode || mode === 'standard'
}

export const useCartStore = defineStore('cart', () => {
  // State
  const items = ref<CartItem[]>([])
  const coupon = ref<Coupon | null>(null)
  const isOpen = ref(false)

  // Carregar carrinho do localStorage
  function loadFromStorage(): void {
    try {
      const stored = localStorage.getItem(CART_STORAGE_KEY)
      if (stored) {
        const parsed: StoredCart = JSON.parse(stored)
        const allItems = parsed.items || []
        const validItems = allItems.filter(isStandardItem)
        const removed = allItems.length - validItems.length
        items.value = validItems
        coupon.value = parsed.coupon || null
        if (removed > 0) {
          try {
            const toast = useToast()
            toast.info(
              removed === 1
                ? '1 item foi removido do carrinho porque o produto mudou de forma de venda.'
                : `${removed} itens foram removidos do carrinho porque os produtos mudaram de forma de venda.`,
            )
          } catch {
            // Pinia/toast pode não estar pronto na primeira hidratação — falha silenciosa
          }
        }
      }
    } catch (e) {
      console.error('Error loading cart from storage:', e)
    }
  }

  // Salvar carrinho no localStorage
  function saveToStorage(): void {
    try {
      const data: StoredCart = {
        items: items.value,
        coupon: coupon.value
      }
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(data))
    } catch (e) {
      console.error('Error saving cart to storage:', e)
    }
  }

  // Watch para salvar automaticamente
  watch([items, coupon], saveToStorage, { deep: true })

  // Getters
  const itemCount = computed(() => {
    return items.value.reduce((sum, item) => sum + item.quantity, 0)
  })

  const subtotal = computed(() => {
    return items.value.reduce((sum, item) => sum + (item.price * item.quantity), 0)
  })

  const discount = computed(() => {
    if (!coupon.value) return 0

    if (coupon.value.discount_type === 'percentage') {
      return subtotal.value * (coupon.value.discount_value / 100)
    } else {
      return Math.min(coupon.value.discount_value, subtotal.value)
    }
  })

  const total = computed(() => {
    return Math.max(0, subtotal.value - discount.value)
  })

  const isEmpty = computed(() => items.value.length === 0)

  // Actions
  function addItem(product: Product, quantity: number = 1): void {
    if (!isStandardItem(product)) {
      try {
        const toast = useToast()
        toast.warning('Este produto não pode ser adicionado ao carrinho. Use o botão de compra na página do produto.')
      } catch {
        // ignore
      }
      return
    }

    const existingIndex = items.value.findIndex(item => item.id === product.id)

    if (existingIndex > -1) {
      // Produto já existe, incrementar quantidade
      items.value[existingIndex].quantity += quantity
    } else {
      // Adicionar novo item
      items.value.push({
        id: product.id,
        name: product.name,
        slug: product.slug,
        price: product.price,
        thumbnail_url: product.thumbnail_url,
        type: product.type,
        purchase_mode: product.purchase_mode,
        quantity
      })
    }
  }

  function removeItem(productId: string): void {
    const index = items.value.findIndex(item => item.id === productId)
    if (index > -1) {
      items.value.splice(index, 1)
    }
  }

  function updateQuantity(productId: string, quantity: number): void {
    const item = items.value.find(item => item.id === productId)
    if (item) {
      if (quantity <= 0) {
        removeItem(productId)
      } else {
        item.quantity = quantity
      }
    }
  }

  function incrementQuantity(productId: string): void {
    const item = items.value.find(item => item.id === productId)
    if (item) {
      item.quantity++
    }
  }

  function decrementQuantity(productId: string): void {
    const item = items.value.find(item => item.id === productId)
    if (item) {
      if (item.quantity > 1) {
        item.quantity--
      } else {
        removeItem(productId)
      }
    }
  }

  function applyCoupon(couponData: Coupon): void {
    coupon.value = couponData
  }

  function removeCoupon(): void {
    coupon.value = null
  }

  function clearCart(): void {
    items.value = []
    coupon.value = null
  }

  function toggleDrawer(): void {
    isOpen.value = !isOpen.value
  }

  function openDrawer(): void {
    isOpen.value = true
  }

  function closeDrawer(): void {
    isOpen.value = false
  }

  // Obter dados para checkout
  function getCheckoutData(): CheckoutData {
    return {
      items: items.value.map(item => ({
        product_id: item.id,
        product_name: item.name,
        quantity: item.quantity,
        unit_price: item.price,
        total_price: item.price * item.quantity
      })),
      subtotal: subtotal.value,
      discount: discount.value,
      total: total.value,
      coupon_code: coupon.value?.code || null
    }
  }

  // Inicializar
  loadFromStorage()

  return {
    // State
    items,
    coupon,
    isOpen,
    // Getters
    itemCount,
    subtotal,
    discount,
    total,
    isEmpty,
    // Actions
    addItem,
    removeItem,
    updateQuantity,
    incrementQuantity,
    decrementQuantity,
    applyCoupon,
    removeCoupon,
    clearCart,
    toggleDrawer,
    openDrawer,
    closeDrawer,
    getCheckoutData,
    loadFromStorage,
  }
})
